// Revisado: 2026-02-05 [Sprint 1 - Layout Optimization]
import React from 'react';

import { useTranslation } from 'react-i18next';

const UserList = ({ users, loading, onUserClick, onForceLogout, logoutingUser }) => {
    const { t } = useTranslation();

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-green"></div>
            </div>
        );
    }

    if (!users || users.length === 0) {
        return (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-500">{t('UserList.msg_no_users')}</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('UserList.header_user')}
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                Disp.
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-52">
                                {t('UserList.header_last_login')}
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                                Acciones
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                            <tr
                                key={user.user_id}
                                className="hover:bg-gray-50 transition-colors"
                            >
                                {/* Usuario Column: Avatar + Name + Email + Role + Created Date + Status Dot */}
                                <td className="px-6 py-4">
                                    <div className="flex items-start">
                                        {/* Avatar with Status Dot */}
                                        <div className="flex-shrink-0 h-12 w-12 relative">
                                            <div className="h-12 w-12 rounded-full bg-primary-green flex items-center justify-center text-white font-bold text-lg">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            {/* Status Dot - Absolute positioned on avatar */}
                                            <div className={`absolute -top-1 -right-1 h-4 w-4 rounded-full border-2 border-white ${user.is_active ? 'bg-green-500' : 'bg-red-500'
                                                }`} title={user.is_active ? 'Activo' : 'Inactivo'}></div>
                                        </div>

                                        {/* User Info Stack */}
                                        <div className="ml-4 space-y-0.5">
                                            {/* Name + Click hint */}
                                            <div
                                                className="text-sm font-semibold text-gray-900 hover:text-primary-green cursor-pointer"
                                                onClick={() => onUserClick && onUserClick(user)}
                                                title="Click para cambiar contraseña"
                                            >
                                                {user.name}
                                            </div>

                                            {/* Email */}
                                            <div className="text-xs text-gray-500">{user.email}</div>

                                            {/* Role Badge */}
                                            <div>
                                                <span className={`px-2 py-0.5 inline-flex text-xs font-medium rounded-full ${user.role === 'admin'
                                                    ? 'bg-purple-100 text-purple-700'
                                                    : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                    {user.role}
                                                </span>
                                            </div>

                                            {/* Created Date */}
                                            <div className="text-xs text-gray-400">
                                                Creado: {new Date(user.created_at).toLocaleDateString('es-CL')}
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                {/* Devices Column - Compact */}
                                <td className="px-4 py-4 text-center">
                                    <span className={`inline-flex items-center justify-center px-2.5 py-1 text-sm font-semibold rounded-full ${user.active_sessions === 0
                                        ? 'bg-gray-100 text-gray-600'
                                        : 'bg-blue-100 text-blue-700'
                                        }`}>
                                        {user.active_sessions || 0}
                                    </span>
                                </td>

                                {/* Last Login Column - Stacked Date/Time */}
                                <td className="px-6 py-4">
                                    {user.last_login ? (
                                        <div className="space-y-0.5">
                                            <div className="text-sm text-gray-700">
                                                {new Date(user.last_login).toLocaleDateString('es-CL')}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {new Date(user.last_login).toLocaleTimeString('es-CL', {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit'
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-400">{t('UserList.date_never')}</span>
                                    )}
                                </td>

                                {/* Actions Column - Stacked Buttons */}
                                <td className="px-6 py-4">
                                    <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                                        {/* Reset Password Button */}
                                        <button
                                            onClick={() => onUserClick && onUserClick(user)}
                                            className="px-4 py-2 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors w-full min-w-[120px]"
                                            title="Cambiar contraseña del usuario"
                                        >
                                            🔑 Reset Pass
                                        </button>

                                        {/* Logout Button */}
                                        <button
                                            onClick={() => onForceLogout && onForceLogout(user.user_id, user.name)}
                                            disabled={logoutingUser === user.user_id}
                                            className="px-4 py-2 bg-orange-600 text-white text-xs font-medium rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full min-w-[120px]"
                                            title="Forzar cierre de sesión en todos los dispositivos"
                                        >
                                            {logoutingUser === user.user_id ? 'Cerrando...' : '🔓 Logout'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UserList;
